import {
  ISSUE_MGMT_WRITE_PERMISSION,
  ISSUE_UNLINKED_LIMIT_MAX,
} from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IssueListTable } from '../components/issue-mgmt/IssueListTable.js';
import { NewEntryDialog } from '../components/issue-mgmt/NewEntryDialog.js';
import { ToggleSwitch } from '../components/issue-mgmt/ToggleSwitch.js';
import { UnlinkedVoices } from '../components/issue-mgmt/UnlinkedVoices.js';
import { useDebouncedValue } from '../hooks/useDebouncedValue.js';
import { useIssueList, useIssueThemes } from '../hooks/useIssueMgmt.js';
import { usePermissions } from '../hooks/usePermissions.js';
import {
  type IssueListSearch,
  parseIssueListSearch,
  toIssueListSearchParams,
} from '../lib/issue-mgmt-search.js';

/**
 * 検索語を URL に入れるまで待つ時間。
 * @graph-connects none
 */
const SEARCH_DELAY_MS = 300;

/**
 * 課題に紐づいていない声を一度に出す件数。
 * @graph-connects none
 */
const UNLINKED_PAGE_SIZE = 20;

/**
 * 課題管理の「課題」タブ（一覧・SC-02）。最初に開く画面。
 *
 * 1つの検索欄で課題と声を探し（番号・旧番号・Backlog キー・タイトル・中身・テーマ・領域・声の本文）、検索語・テーマ・
 * CS向け表示・隠したものの表示は URL に入れる（同じ URL を開けば同じ一覧）。編集キーの人には「＋ 声を入れる」を出す。
 * 検索したときは、課題に紐づいていない声を表の下に出す。
 *
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の一覧で、課題と声を1つの欄で探し、状態・担当・声の数を見て、行を押して詳細を開く。CS は今の案内を一覧で見られる
 * @graph-connects useIssueList [calls] 一覧と検索
 * @graph-connects useIssueThemes [calls] テーマで絞ったときの名前
 * @graph-connects usePermissions [calls] 編集キーの有無
 * @graph-connects IssueListTable [calls] 一覧の表
 * @graph-connects UnlinkedVoices [calls] 課題に紐づいていない声
 * @graph-connects NewEntryDialog [calls] 声を入れる・課題を作る
 * @graph-connects ToggleSwitch [calls] CS向け表示の切り替え
 */
export function IssueMgmtListPage({ now = new Date() }: { now?: Date }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = parseIssueListSearch(useSearch({ strict: false }));
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canEdit = !permissionsLoading && hasPermission(ISSUE_MGMT_WRITE_PERMISSION);
  const themes = useIssueThemes();
  const [entryOpen, setEntryOpen] = useState(false);

  const setSearch = useCallback(
    (next: IssueListSearch) => {
      void navigate({
        to: '/issue-mgmt/issues',
        search: toIssueListSearchParams(next),
        replace: true,
      });
    },
    [navigate],
  );

  // 検索欄は打っている文字を手元に持ち、落ち着いたら URL に入れる。URL が外から変わった（戻る・検索をやめる）ときは追従する
  const [draft, setDraft] = useState(search.q);
  const syncedQuery = useRef(search.q);
  if (syncedQuery.current !== search.q) {
    syncedQuery.current = search.q;
    if (draft.trim() !== search.q) setDraft(search.q);
  }
  const debounced = useDebouncedValue(draft.trim(), SEARCH_DELAY_MS);
  const lastDebounced = useRef(debounced);
  useEffect(() => {
    if (lastDebounced.current === debounced) return;
    lastDebounced.current = debounced;
    if (debounced !== search.q) {
      setSearch({ q: debounced, theme: search.theme, cs: search.cs, hidden: search.hidden });
    }
  }, [debounced, search.q, search.theme, search.cs, search.hidden, setSearch]);

  // 「もっと見る」で増やした件数は、その検索語のあいだだけ使う
  const [unlinked, setUnlinked] = useState({ query: search.q, limit: UNLINKED_PAGE_SIZE });
  const unlinkedLimit = unlinked.query === search.q ? unlinked.limit : UNLINKED_PAGE_SIZE;

  const list = useIssueList({
    query: search.q,
    themeId: search.theme,
    includeHidden: search.hidden,
    unlinkedLimit,
  });
  const themeName =
    search.theme === null
      ? null
      : (themes.data?.themes.find((theme) => theme.id === search.theme)?.name ?? search.theme);

  const clearSearch = () => {
    setDraft('');
    setSearch({ ...search, q: '' });
  };
  const openIssue = (issueId: string) => {
    void navigate({ to: '/issue-mgmt/issues/$issueId', params: { issueId } });
  };

  const data = list.data;
  const unlinkedVoices = data?.unlinkedVoices ?? null;
  const nothingFound =
    data !== undefined &&
    search.q !== '' &&
    data.issues.length === 0 &&
    (unlinkedVoices === null || unlinkedVoices.total === 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[16rem] flex-1">
          <Input
            aria-label={t('issueMgmt.list.searchLabel')}
            placeholder={t('issueMgmt.list.searchPlaceholder')}
            leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            fullWidth
          />
        </div>
        <ToggleSwitch
          checked={search.cs}
          onToggle={() => {
            setSearch({ ...search, cs: !search.cs });
          }}
        >
          {t('issueMgmt.list.csToggle')}
        </ToggleSwitch>
        {canEdit && (
          <Button
            onClick={() => {
              setEntryOpen(true);
            }}
          >
            {t('issueMgmt.list.newEntry')}
          </Button>
        )}
      </div>

      {themeName !== null && (
        <div>
          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-accent-primary)] px-3 py-1 text-xs text-[var(--color-accent-primary)]">
            {t('issueMgmt.list.themeFilter', { name: themeName })}
            <button
              type="button"
              aria-label={t('issueMgmt.list.themeFilterClear')}
              onClick={() => {
                setSearch({ ...search, theme: null });
              }}
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
        </div>
      )}

      {data?.slackUrlNotFound === true && (
        <p className="rounded-lg bg-[var(--color-bg-elevated)] px-4 py-3 text-sm text-[var(--color-text-secondary)]">
          {t('issueMgmt.list.slackUrlNotFound')}
        </p>
      )}

      {list.isError && (
        <div className="space-y-2 rounded-xl border border-[var(--color-border)] p-6 text-center">
          <p className="text-sm text-[var(--color-error)]">{t('issueMgmt.common.loadError')}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void list.refetch();
            }}
          >
            {t('issueMgmt.common.retry')}
          </Button>
        </div>
      )}
      {!list.isError && data === undefined && (
        <p className="py-10 text-center text-sm text-[var(--color-text-muted)]">
          {t('issueMgmt.common.loading')}
        </p>
      )}

      {nothingFound && (
        <div className="space-y-2 rounded-xl border border-[var(--color-border)] p-8 text-center">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('issueMgmt.list.noMatch', { query: search.q })}
          </p>
          <Button variant="secondary" size="sm" onClick={clearSearch}>
            {t('issueMgmt.list.clearSearch')}
          </Button>
        </div>
      )}

      {data !== undefined && !nothingFound && (
        <>
          {data.issues.length === 0 && search.q === '' && search.theme === null ? (
            <p className="rounded-xl border border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-text-muted)]">
              {t('issueMgmt.list.empty')}
            </p>
          ) : (
            <IssueListTable
              issues={data.issues}
              query={search.q}
              showGuidance={search.cs}
              onOpen={openIssue}
              now={now}
            />
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--color-text-muted)]">
            <div className="flex items-center gap-3">
              <span>{t('issueMgmt.list.count', { n: String(data.issues.length) })}</span>
              {search.hidden ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearch({ ...search, hidden: false });
                  }}
                  className="text-[var(--color-accent-primary)] hover:underline"
                >
                  {t('issueMgmt.list.hideHidden')}
                </button>
              ) : (
                data.hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch({ ...search, hidden: true });
                    }}
                    className="text-[var(--color-accent-primary)] hover:underline"
                  >
                    {t('issueMgmt.list.showHidden', { n: String(data.hiddenCount) })}
                  </button>
                )
              )}
            </div>
            <span>{t('issueMgmt.list.rowHint')}</span>
          </div>
          {unlinkedVoices !== null && unlinkedVoices.total > 0 && (
            <UnlinkedVoices
              unlinked={unlinkedVoices}
              query={search.q}
              now={now}
              canEdit={canEdit}
              onShowMore={
                unlinkedLimit >= ISSUE_UNLINKED_LIMIT_MAX
                  ? null
                  : () => {
                      setUnlinked({
                        query: search.q,
                        limit: Math.min(
                          unlinkedLimit + UNLINKED_PAGE_SIZE,
                          ISSUE_UNLINKED_LIMIT_MAX,
                        ),
                      });
                    }
              }
            />
          )}
        </>
      )}

      <NewEntryDialog
        open={entryOpen}
        now={now}
        onClose={() => {
          setEntryOpen(false);
        }}
        onIssueCreated={openIssue}
      />
    </div>
  );
}
